// frontend/src/components/Requisitions/RequisitionForm.jsx
import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import LoadingSpinner from '../UI/LoadingSpinner';
import { useToast } from '../../contexts/ToastContext';
import { AuthContext } from '../../contexts/AuthContext';

/**
 * Props:
 * - mode: "create" | "edit" (default: "create")
 * - initialData: requisition object from GET /requisitions/:id/ (required for edit)
 * - embed: boolean (kept from your original)
 * - formData, setFormData: (optional) "controlled-if-provided" pattern
 * - ackCostRealistic: (optional) boolean. If provided, will be sent as ack_cost_realistic.
 */

// Helpers Fase 5 (FK como fuente de verdad; texto solo display)
const toPositiveIntOrEmpty = (v) => {
  if (v === null || typeof v === 'undefined' || v === '') return '';
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : '';
};

const toPositiveIntOrNull = (v) => {
  const n = toPositiveIntOrEmpty(v);
  return n === '' ? null : n;
};

const getUserFullName = (u) => ((u?.first_name || '') + ' ' + (u?.last_name || '')).trim();

// Mejora de robustez: department_display -> department_ref(code/name) -> department legacy
const getUserDepartmentDisplay = (u) => {
  const display = (u?.department_display || '').toString().trim();
  if (display) return display;

  const code = (u?.department_ref?.code || '').toString().trim();
  const name = (u?.department_ref?.name || '').toString().trim();
  if (code && name) return `${code} - ${name}`;
  if (code || name) return code || name;

  return (u?.department || '').toString().trim(); // legacy fallback temporal
};

const buildDepartmentDisplayFromObj = (dep) => {
  if (!dep || typeof dep !== 'object') return '';
  const code = String(dep.code || '').trim();
  const name = String(dep.name || '').trim();
  if (code && name) return `${code} - ${name}`;
  return code || name || '';
};

// Normalizar payloads DRF paginados o no paginados
const getResultsFromPayload = (payload) => {
  if (Array.isArray(payload)) {
    return { results: payload, next: null };
  }
  if (payload && Array.isArray(payload.results)) {
    return {
      results: payload.results,
      next: payload.next || null,
    };
  }
  return { results: [], next: null };
};

const isCatalogItemActive = (item) => {
  if (!item || typeof item !== 'object') return false;
  if (!Object.prototype.hasOwnProperty.call(item, 'active')) return true;

  const value = item.active;
  if (typeof value === 'string') {
    return !['false', '0', 'no', 'off', 'inactive'].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
};

// Fetch de catalogo soportando paginacion DRF
const fetchAllCatalogPages = async (endpoint) => {
  let nextUrl = endpoint;
  const all = [];

  while (nextUrl) {
    const res = await apiClient.get(nextUrl);
    const { results, next } = getResultsFromPayload(res.data);
    all.push(...results);
    nextUrl = next;
  }

  // de-duplicar por id por robustez
  const seen = new Set();
  return all.filter((item) => {
    const key = String(item?.id ?? '');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeComparableText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s./-]/g, '')
    .toUpperCase()
    .trim();

const EXPLICIT_AGREEMENT_FUNDING_CODES = new Set(['16000', '16001']);
const NO_APLICA_AGREEMENT_CODE = '0000';
const NO_APLICA_AGREEMENT_DESCRIPTION = 'NO APLICA';

const getCatalogCode = (item) => String(item?.code || '').trim();
const buildAgreementLabel = (agreement) =>
  agreement ? `${agreement.code} - ${agreement.description}` : '';
const isNoAplicaAgreement = (agreement) =>
  getCatalogCode(agreement) === NO_APLICA_AGREEMENT_CODE &&
  normalizeComparableText(agreement?.description) ===
    normalizeComparableText(NO_APLICA_AGREEMENT_DESCRIPTION);

const isAllowedTenderOption = (tender) => {
  if (!isCatalogItemActive(tender)) return false;

  const normalizedName = normalizeComparableText(tender.name);
  if (!normalizedName) return false;

  return true;
};

const buildInitialFormState = (user) => ({
  // Display-only (pero canonico si backend ya manda department_display)
  department: getUserDepartmentDisplay(user),

  // Nuevo FK para logica / payloads
  department_ref_id: toPositiveIntOrEmpty(user?.department_ref_id),

  project: '',
  funding_source: '',
  budget_unit: '',
  agreement: '',
  tender: '',
  title: '',
  description: '',
  external_service: '',
  fecha: new Date().toLocaleDateString('es-MX'),
  solicitante: getUserFullName(user),

  // labels for Step 2 / summaries
  project_label: '',
  funding_source_label: '',
  budget_unit_label: '',
  agreement_label: '',
  tender_label: '',
  external_service_label: '',
});

export default function RequisitionForm({
  mode = 'create',
  initialData = null,
  embed = false,
  formData,
  setFormData,
  ackCostRealistic, // optional
}) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useContext(AuthContext);

  const fetchedCatalogs = useRef(false);

  const [catalogs, setCatalogs] = useState({
    projects: [],
    funding_sources: [],
    budget_units: [],
    agreements: [],
    tenders: [],
    external_services: [],
  });

  const [catalogsLoaded, setCatalogsLoaded] = useState(false);

  // Controlled-if-provided pattern
  const [internalFormData, setInternalFormData] = useState(() => buildInitialFormState(user));

  const isControlled = Boolean(formData && setFormData);
  const data = isControlled ? formData : internalFormData;
  const update = isControlled ? setFormData : setInternalFormData;

  const [loading, setLoading] = useState(false);

  const visibleProjects = useMemo(
    () => (catalogs.projects || []).filter((item) => isCatalogItemActive(item)),
    [catalogs.projects]
  );

  const visibleFundingSources = useMemo(
    () => (catalogs.funding_sources || []).filter((item) => isCatalogItemActive(item)),
    [catalogs.funding_sources]
  );

  const visibleBudgetUnits = useMemo(
    () => (catalogs.budget_units || []).filter((item) => isCatalogItemActive(item)),
    [catalogs.budget_units]
  );

  const visibleTenders = useMemo(
    () =>
      (catalogs.tenders || []).filter((t) =>
        isAllowedTenderOption(t)
      ),
    [catalogs.tenders]
  );

  const findById = (list, id) =>
    (list || []).find((item) => String(item?.id) === String(id));

  const selectedFundingSource = useMemo(
    () => findById(catalogs.funding_sources, data.funding_source),
    [catalogs.funding_sources, data.funding_source]
  );

  const shouldShowAgreementField = useMemo(
    () => EXPLICIT_AGREEMENT_FUNDING_CODES.has(getCatalogCode(selectedFundingSource)),
    [selectedFundingSource]
  );

  const noAplicaAgreement = useMemo(
    () =>
      (catalogs.agreements || []).find(
        (item) => isCatalogItemActive(item) && isNoAplicaAgreement(item)
      ) || null,
    [catalogs.agreements]
  );

  const visibleAgreements = useMemo(
    () =>
      (catalogs.agreements || []).filter((item) => {
        if (!isCatalogItemActive(item)) return false;
        if (!shouldShowAgreementField) return true;
        return !isNoAplicaAgreement(item);
      }),
    [catalogs.agreements, shouldShowAgreementField]
  );

  const visibleExternalServices = useMemo(
    () => (catalogs.external_services || []).filter((item) => isCatalogItemActive(item)),
    [catalogs.external_services]
  );

  // Si por alguna razon queda una licitacion no permitida, limpiala
  useEffect(() => {
    if (!data.tender) return;
    const t = findById(catalogs.tenders, data.tender);
    if (t && !isAllowedTenderOption(t)) {
      update((prev) => ({ ...prev, tender: '', tender_label: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogs.tenders]);

  useEffect(() => {
    if (!catalogsLoaded) return;

    update((prev) => {
      const hasFundingSource = Boolean(prev.funding_source);
      const currentAgreementId = String(prev.agreement || '');
      const noAplicaAgreementId = String(noAplicaAgreement?.id || '');
      const noAplicaAgreementLabel = buildAgreementLabel(noAplicaAgreement);

      if (!hasFundingSource) {
        if (!prev.agreement && !prev.agreement_label) return prev;
        return {
          ...prev,
          agreement: '',
          agreement_label: '',
        };
      }

      if (!shouldShowAgreementField) {
        if (!noAplicaAgreement) return prev;

        if (
          currentAgreementId === noAplicaAgreementId &&
          prev.agreement_label === noAplicaAgreementLabel
        ) {
          return prev;
        }

        return {
          ...prev,
          agreement: noAplicaAgreement.id,
          agreement_label: noAplicaAgreementLabel,
        };
      }

      if (!prev.agreement) return prev;

      const isCurrentAgreementNoAplica =
        noAplicaAgreementId && currentAgreementId === noAplicaAgreementId;
      const currentAgreementStillVisible = visibleAgreements.some(
        (item) => String(item.id) === currentAgreementId
      );

      if (!isCurrentAgreementNoAplica && currentAgreementStillVisible) {
        return prev;
      }

      return {
        ...prev,
        agreement: '',
        agreement_label: '',
      };
    });
  }, [catalogsLoaded, noAplicaAgreement, shouldShowAgreementField, update, visibleAgreements]);

  // Redirect if no token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      showToast?.('Tu sesion ha expirado. Por favor inicia sesion de nuevo.', 'warning');
      navigate('/login');
    }
  }, [navigate, showToast]);

  // Keep department/solicitante updated if user changes
  useEffect(() => {
    const userDeptDisplay = getUserDepartmentDisplay(user);
    const userDeptFk = toPositiveIntOrEmpty(user?.department_ref_id);
    const userFullName = getUserFullName(user);

    if (userDeptDisplay || userDeptFk || userFullName) {
      update((prev) => ({
        ...prev,
        department: userDeptDisplay || prev.department,
        department_ref_id: userDeptFk || prev.department_ref_id,
        solicitante: userFullName || prev.solicitante,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setRequiredMessage = (e, message) => {
    e.target.setCustomValidity(message);
    e.target.oninput = () => e.target.setCustomValidity('');
  };

  // Fetch catalogs once
  useEffect(() => {
    if (fetchedCatalogs.current) return;
    fetchedCatalogs.current = true;

    (async () => {
      try {
        const [
          projects,
          funding_sources,
          budget_units,
          agreements,
          tenders,
          external_services,
        ] = await Promise.all([
          fetchAllCatalogPages('/catalogs/projects/'),
          fetchAllCatalogPages('/catalogs/funding-sources/'),
          fetchAllCatalogPages('/catalogs/budget-units/'),
          fetchAllCatalogPages('/catalogs/agreements/'),
          fetchAllCatalogPages('/catalogs/tenders/'),
          fetchAllCatalogPages('/catalogs/external-services/'),
        ]);

        setCatalogs({
          projects,
          funding_sources,
          budget_units,
          agreements,
          tenders,
          external_services,
        });
      } catch (err) {
        console.error('Error loading catalogos:', err);
        showToast?.('Error al cargar catalogos.', 'error');
      } finally {
        setCatalogsLoaded(true);
      }
    })();
  }, [showToast]);

  // Helper: set both value + label when you know an ID and you have catalogs
  const setValueAndLabel = (valueKey, labelKey, id, list, labelBuilder) => {
    const found = (list || []).find((x) => String(x.id) === String(id));
    const label = found ? labelBuilder(found) : '';
    update((prev) => ({ ...prev, [valueKey]: id || '', [labelKey]: label }));
  };

  // When editing: hydrate base fields
  useEffect(() => {
    if (mode !== 'edit' || !initialData) return;

    const rd = initialData.requesting_department;
    const rdId = rd && typeof rd === 'object' ? toPositiveIntOrEmpty(rd.id) : toPositiveIntOrEmpty(rd);

    const rdDisplayFromNested = buildDepartmentDisplayFromObj(rd);
    const rdDisplayFromFlat =
      String(
        initialData.requesting_department_display ||
        initialData.requesting_department_label ||
        ''
      ).trim();

    const resolvedDeptDisplay = rdDisplayFromFlat || rdDisplayFromNested;

    update((prev) => ({
      ...prev,
      department: resolvedDeptDisplay || prev.department,
      department_ref_id: rdId || prev.department_ref_id,
      description: initialData.requisition_reason ?? initialData.description ?? prev.description,
      fecha: prev.fecha,
      title: prev.title,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialData]);

  // Once catalogs are available, set IDs + labels from initialData
  useEffect(() => {
    if (mode !== 'edit' || !initialData || !catalogsLoaded) return;

    const {
      project,
      funding_source,
      budget_unit,
      agreement,
      tender,
      external_service,
    } = initialData;

    const getId = (objOrId) => (objOrId && typeof objOrId === 'object' ? objOrId.id : objOrId);

    setValueAndLabel(
      'project',
      'project_label',
      getId(project),
      catalogs.projects,
      (p) => `${p.code ? `${p.code} - ` : ''}${p.description || ''}`.trim()
    );
    setValueAndLabel(
      'funding_source',
      'funding_source_label',
      getId(funding_source),
      catalogs.funding_sources,
      (fs) => `${fs.code} - ${fs.description}`
    );
    setValueAndLabel(
      'budget_unit',
      'budget_unit_label',
      getId(budget_unit),
      catalogs.budget_units,
      (bu) => `${bu.code} - ${bu.description}`
    );
    setValueAndLabel(
      'agreement',
      'agreement_label',
      getId(agreement),
      catalogs.agreements,
      (a) => `${a.code} - ${a.description}`
    );
    setValueAndLabel('tender', 'tender_label', getId(tender), catalogs.tenders, (t) => t.name);
    setValueAndLabel(
      'external_service',
      'external_service_label',
      getId(external_service),
      catalogs.external_services,
      (s) => s.name
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialData, catalogs, catalogsLoaded]);

  // Limpieza robusta de valores huerfanos o ya no visibles
  useEffect(() => {
    if (!catalogsLoaded) return;

    const clearedLabels = [];

    update((prev) => {
      let changed = false;
      const next = { ...prev };

      const clearField = (valueKey, labelKey, labelForToast) => {
        if (next[valueKey] || next[labelKey]) {
          next[valueKey] = '';
          next[labelKey] = '';
          changed = true;
          clearedLabels.push(labelForToast);
        }
      };

      const currentProject = prev.project ? findById(catalogs.projects, prev.project) : null;
      if (prev.project && (!currentProject || !visibleProjects.some((x) => String(x.id) === String(prev.project)))) {
        clearField('project', 'project_label', 'Proyecto');
      }

      const currentFunding = prev.funding_source ? findById(catalogs.funding_sources, prev.funding_source) : null;
      if (
        prev.funding_source &&
        (!currentFunding || !visibleFundingSources.some((x) => String(x.id) === String(prev.funding_source)))
      ) {
        clearField('funding_source', 'funding_source_label', 'Fuente de financiamiento');
        clearField('agreement', 'agreement_label', 'Convenio');
      }

      const currentBudgetUnit = prev.budget_unit ? findById(catalogs.budget_units, prev.budget_unit) : null;
      if (
        prev.budget_unit &&
        (!currentBudgetUnit || !visibleBudgetUnits.some((x) => String(x.id) === String(prev.budget_unit)))
      ) {
        clearField('budget_unit', 'budget_unit_label', 'Unidad presupuestal');
      }

      const currentAgreement = prev.agreement ? findById(catalogs.agreements, prev.agreement) : null;
      if (prev.agreement) {
        if (!currentAgreement) {
          clearField('agreement', 'agreement_label', 'Convenio');
        } else if (!visibleAgreements.some((x) => String(x.id) === String(prev.agreement))) {
          clearField('agreement', 'agreement_label', 'Convenio');
        }
      }

      const currentTender = prev.tender ? findById(catalogs.tenders, prev.tender) : null;
      if (
        prev.tender &&
        (!currentTender || !visibleTenders.some((x) => String(x.id) === String(prev.tender)))
      ) {
        clearField('tender', 'tender_label', 'Licitacion');
      }

      const currentExternalService = prev.external_service
        ? findById(catalogs.external_services, prev.external_service)
        : null;
      if (
        prev.external_service &&
        (!currentExternalService ||
          !visibleExternalServices.some((x) => String(x.id) === String(prev.external_service)))
      ) {
        clearField('external_service', 'external_service_label', 'Servicio externo / academico');
      }

      return changed ? next : prev;
    });

    if (clearedLabels.length > 0) {
      const uniqueLabels = [...new Set(clearedLabels)];
      showToast?.(
        `Se limpiaron selecciones que ya no estan activas o disponibles: ${uniqueLabels.join(', ')}.`,
        'warning'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    catalogsLoaded,
    catalogs,
    visibleProjects,
    visibleFundingSources,
    visibleBudgetUnits,
    visibleTenders,
    visibleAgreements,
    visibleExternalServices,
    data.project,
    data.funding_source,
    data.budget_unit,
    data.agreement,
    data.tender,
    data.external_service,
    showToast,
  ]);

  const handleChange = (e) => {
    update((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSelectChange = (e, valueKey, labelKey) => {
    const val = e.target.value;
    const label = val ? (e.target.options[e.target.selectedIndex]?.text || '') : '';
    update((prev) => ({ ...prev, [valueKey]: val, [labelKey]: label }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const requestingDepartmentFromData = toPositiveIntOrNull(data.department_ref_id);
      const requestingDepartmentFromUser = toPositiveIntOrNull(user?.department_ref_id);

      const resolvedRequestingDepartment =
        requestingDepartmentFromData ?? requestingDepartmentFromUser;

      const basePayload = {
        project: data.project || null,
        funding_source: data.funding_source || null,
        budget_unit: data.budget_unit || null,
        agreement: shouldShowAgreementField
          ? (data.agreement || null)
          : (noAplicaAgreement?.id || data.agreement || null),
        tender: data.tender || null,
        external_service: data.external_service || null,
        requisition_reason: data.description || '',
        ...(typeof ackCostRealistic === 'boolean' ? { ack_cost_realistic: ackCostRealistic } : {}),
      };

      if (data.funding_source && !shouldShowAgreementField && !basePayload.agreement) {
        showToast?.(
          "No se encontró el convenio activo '0000 - NO APLICA' requerido para la fuente seleccionada.",
          'error'
        );
        return;
      }

      if (mode === 'edit' && initialData?.id) {
        const rd = initialData.requesting_department;
        const originalRequestingDepartment =
          (rd && typeof rd === 'object' ? rd.id : rd) ?? undefined;

        const requesting_department =
          resolvedRequestingDepartment || originalRequestingDepartment || undefined;

        await apiClient.put(
          `/requisitions/${initialData.id}/`,
          {
            ...basePayload,
            ...(requesting_department ? { requesting_department } : {}),
          }
        );
        showToast?.('Requisicion actualizada correctamente!', 'success');
      } else {
        if (!resolvedRequestingDepartment) {
          showToast?.(
            'No se pudo determinar el departamento solicitante (FK). Vuelve a iniciar sesion o revisa tu usuario.',
            'error'
          );
          return;
        }

        await apiClient.post('/requisitions/', {
          ...basePayload,
          requesting_department: resolvedRequestingDepartment,
        });

        showToast?.('Requisicion creada correctamente!', 'success');
      }

      navigate('/requisitions');
    } catch (err) {
      console.error('Error guardando la requisicion:', err);
      showToast?.('Error al guardar la requisicion.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <>
      <input type="hidden" name="department_ref_id" value={data.department_ref_id || ''} readOnly />

      <label className="block mb-1 font-medium">Departamento</label>
      <input
        type="text"
        name="department"
        value={data.department}
        readOnly
        required
        onInvalid={(e) => setRequiredMessage(e, 'El departamento es obligatorio')}
        className="border p-2 w-full rounded bg-gray-100 cursor-not-allowed"
      />

      <label className="block mb-1 font-medium">Proyecto</label>
      <select
        name="project"
        value={data.project}
        onChange={(e) => handleSelectChange(e, 'project', 'project_label')}
        required
        onInvalid={(e) => setRequiredMessage(e, 'Por favor seleccione un proyecto')}
        className="border p-2 w-full rounded"
      >
        <option value="">Seleccione Proyecto</option>
        {visibleProjects.map((proj) => (
          <option key={proj.id} value={proj.id}>
            {proj.code ? `${proj.code} - ${proj.description}` : proj.description}
          </option>
        ))}
      </select>

      <label className="block mb-1 font-medium">Fuente de Financiamiento</label>
      <select
        name="funding_source"
        value={data.funding_source}
        onChange={(e) => handleSelectChange(e, 'funding_source', 'funding_source_label')}
        required
        onInvalid={(e) => setRequiredMessage(e, 'Por favor seleccione una fuente de financiamiento')}
        className="border p-2 w-full rounded"
      >
        <option value="">Seleccione Fuente de Financiamiento</option>
        {visibleFundingSources.map((fs) => (
          <option key={fs.id} value={fs.id}>
            {fs.code} - {fs.description}
          </option>
        ))}
      </select>

      <label className="block mb-1 font-medium">Unidad Presupuestal</label>
      <select
        name="budget_unit"
        value={data.budget_unit}
        onChange={(e) => handleSelectChange(e, 'budget_unit', 'budget_unit_label')}
        required
        onInvalid={(e) => setRequiredMessage(e, 'Por favor seleccione una unidad presupuestal')}
        className="border p-2 w-full rounded"
      >
        <option value="">Seleccione Unidad Presupuestal</option>
        {visibleBudgetUnits.map((bu) => (
          <option key={bu.id} value={bu.id}>
            {bu.code} - {bu.description}
          </option>
        ))}
      </select>

      {shouldShowAgreementField && (
        <>
          <label className="block mb-1 font-medium">Convenios</label>
          <select
            name="agreement"
            value={data.agreement}
            onChange={(e) => handleSelectChange(e, 'agreement', 'agreement_label')}
            required
            onInvalid={(e) => setRequiredMessage(e, 'Por favor seleccione un convenio')}
            className="border p-2 w-full rounded"
          >
            <option value="">Seleccione Convenio</option>
            {visibleAgreements.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} - {a.description}
              </option>
            ))}
          </select>
        </>
      )}

      <label className="block mb-1 font-medium">Licitacion</label>
      <select
        name="tender"
        value={data.tender}
        onChange={(e) => handleSelectChange(e, 'tender', 'tender_label')}
        required
        onInvalid={(e) => setRequiredMessage(e, 'Por favor seleccione una licitacion')}
        className="border p-2 w-full rounded"
      >
        <option value="">Seleccione Licitacion</option>
        {visibleTenders.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>

      <label className="block mb-1 font-medium">Fecha</label>
      <input
        type="text"
        name="fecha"
        value={data.fecha}
        readOnly
        required
        onInvalid={(e) => setRequiredMessage(e, 'La fecha es obligatoria')}
        className="border p-2 w-full rounded bg-gray-100 cursor-not-allowed"
      />

      <label className="block mb-1 font-medium">Motivos Requisicion</label>
      <textarea
        name="description"
        value={data.description}
        onChange={handleChange}
        required
        onInvalid={(e) => setRequiredMessage(e, 'La descripcion es obligatoria')}
        className="border p-2 w-full rounded"
      />

      <label className="block mb-1 font-medium">Solicitante</label>
      <input
        type="text"
        name="solicitante"
        value={data.solicitante}
        readOnly
        required
        onInvalid={(e) => setRequiredMessage(e, 'El solicitante es obligatorio')}
        className="border p-2 w-full rounded bg-gray-100 cursor-not-allowed"
      />

      <label className="block mb-1 font-medium">Servicio Externo / Academico</label>
      <select
        name="external_service"
        value={data.external_service}
        onChange={(e) => handleSelectChange(e, 'external_service', 'external_service_label')}
        required
        onInvalid={(e) => setRequiredMessage(e, 'Por favor seleccione un servicio')}
        className="border p-2 w-full rounded"
      >
        <option value="">Seleccione Servicio</option>
        {visibleExternalServices.map((svc) => (
          <option key={svc.id} value={svc.id}>
            {svc.name}
          </option>
        ))}
      </select>
    </>
  );

  if (embed) return <div className="space-y-4">{content}</div>;

  return (
    <form onSubmit={handleSubmit} className="max-w-lg mx-auto bg-white p-6 rounded-lg shadow space-y-4">
      <h2 className="text-2xl font-semibold mb-4">
        {mode === 'edit' ? 'Editar Requisicion' : 'Crear Requisicion'}
      </h2>
      {content}
      <button
        type="submit"
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded w-full disabled:opacity-50"
      >
        {loading ? <LoadingSpinner /> : (mode === 'edit' ? 'Guardar Cambios' : 'Enviar')}
      </button>
    </form>
  );
}
